// Phase MHM-3 · Finance — KPI summary + 4 tabs.
// MHM-5.1 — Consumes `?tab=Summary|COD|Payouts|Expenses|Refunds|Damage`
//           from URL so dashboard KPI deep-links pre-select the right tab.
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

const TABS = ["Summary", "COD", "Payouts", "Expenses", "Refunds", "Damage"];

function KPI({ label, value, money, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3 shadow-sm" data-testid={testid}>
      <div className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-xl font-bold text-slate-900 font-['Outfit'] mt-1">
        {money ? "₹" : ""}{(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}

function ExpenseModal({ open, onClose, onCreated }) {
  const [category, setCategory] = useState("misc");
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState("");
  if (!open) return null;
  const submit = async () => {
    try {
      await api.post("/hub/finance/expenses", { category, amount: Number(amount), notes });
      toast.success("Expense added"); onCreated(); onClose();
    } catch (e) { toast.error(e.userMessage || "Add failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mhm-finance-expense-modal">
      <div className="bg-white rounded-2xl p-5 max-w-md w-full">
        <div className="flex justify-between mb-3">
          <h3 className="font-semibold">Add expense</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5" data-testid="mhm-finance-expense-cat">
            {["rent", "utility", "salary", "fuel", "misc"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="number" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)}
                 className="w-full border border-slate-200 rounded-lg px-2 py-1.5" data-testid="mhm-finance-expense-amount"/>
          <input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                 className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
        </div>
        <button onClick={submit} className="mt-3 w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-1.5 text-sm" data-testid="mhm-finance-expense-submit">
          Add
        </button>
      </div>
    </div>
  );
}

function PayoutModal({ open, onClose, onCreated, agent }) {
  const [amount, setAmount] = useState(agent?.pending_payout || 0);
  const [method, setMethod] = useState("wallet_credit");
  const [notes, setNotes] = useState("");
  useEffect(() => setAmount(agent?.pending_payout || 0), [agent]);
  if (!open || !agent) return null;
  const submit = async () => {
    try {
      await api.post(`/hub/finance/agent-payouts/${agent.agent_user_id}/payout`,
                     { amount: Number(amount), method, notes });
      toast.success("Payout created"); onCreated(); onClose();
    } catch (e) { toast.error(e.userMessage || "Payout failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="mhm-finance-payout-modal">
      <div className="bg-white rounded-2xl p-5 max-w-md w-full">
        <div className="flex justify-between mb-3">
          <h3 className="font-semibold">Pay out · {agent.name}</h3>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                 className="w-full border border-slate-200 rounded-lg px-2 py-1.5"
                 data-testid="mhm-finance-payout-amount"/>
          <div className="space-x-2 text-xs">
            <label><input type="radio" checked={method === "wallet_credit"} onChange={() => setMethod("wallet_credit")}/> Wallet credit</label>
            <label><input type="radio" checked={method === "bank_transfer_mock"} onChange={() => setMethod("bank_transfer_mock")}/> Bank transfer (mock)</label>
          </div>
          <input placeholder="Notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                 className="w-full border border-slate-200 rounded-lg px-2 py-1.5"/>
        </div>
        <button onClick={submit} className="mt-3 w-full bg-violet-600 hover:bg-violet-700 text-white rounded-lg py-1.5 text-sm" data-testid="mhm-finance-payout-submit">
          Pay out
        </button>
      </div>
    </div>
  );
}

export default function HubFinance() {
  const [tab, setTab] = useState("Summary");
  const [searchParams] = useSearchParams();
  // Pre-select tab from URL (KPI deep-link). Re-run when ?tab changes.
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TABS.includes(t) && t !== tab) setTab(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [summary, setSummary] = useState(null);
  const [charts, setCharts] = useState(null);
  const [cod, setCod] = useState({ rows: [], by_status: {} });
  const [selected, setSelected] = useState(new Set());
  const [payouts, setPayouts] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [damage, setDamage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [payoutAgent, setPayoutAgent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, codR, pR, eR, rR, dR] = await Promise.all([
        api.get("/hub/finance/summary"),
        api.get("/hub/finance/charts?range=30d"),
        api.get("/hub/finance/cod-reconciliation?status=pending"),
        api.get("/hub/finance/agent-payouts"),
        api.get("/hub/finance/expenses"),
        api.get("/hub/finance/refunds"),
        api.get("/hub/finance/damage-claims"),
      ]);
      setSummary(s.data); setCharts(c.data); setCod(codR.data);
      setPayouts(pR.data.rows || []); setExpenses(eR.data.rows || []);
      setRefunds(rR.data.rows || []); setDamage(dR.data.rows || []);
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const codBulk = async (action) => {
    if (selected.size === 0) return;
    try {
      await api.post("/hub/finance/cod-reconciliation/bulk",
                      { entry_ids: [...selected], action });
      toast.success(`Bulk ${action}: ${selected.size} entries`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e.userMessage || "Bulk failed"); }
  };

  const decideRefund = async (r, decision) => {
    const note = decision === "reject"
      ? window.prompt("Reason:") : null;
    if (decision === "reject" && !note) return;
    const path = decision === "approve"
      ? `/hub/finance/refunds/${r.refund_id || r._id}/approve`
      : `/hub/finance/refunds/${r.refund_id || r._id}/reject`;
    try {
      await api.post(path, decision === "approve" ? {} : { reason: note });
      toast.success(`Refund ${decision}d`);
      load();
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };

  const decideDamage = async (d, decision) => {
    const note = window.prompt(`${decision} note:`);
    if (!note) return;
    try {
      await api.post(`/hub/finance/damage-claims/${d.claim_id}/decide`,
                      { decision, notes: note });
      toast.success(`Claim ${decision}`); load();
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-finance-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-finance-title">
              Finance
            </h1>
            <p className="text-sm text-slate-500">Hub-scoped financial workspace</p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <button onClick={load} data-testid="mhm-finance-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
            </button>
          </div>
        </div>

        <div className="flex gap-1 flex-wrap" data-testid="mhm-finance-tabs">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
                    data-testid={`mhm-finance-tab-${t.toLowerCase()}`}
                    className={`text-sm px-3 py-1.5 rounded-full ${
                      tab === t ? "bg-violet-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                    }`}>{t}</button>
          ))}
        </div>

        {tab === "Summary" && summary && (
          <div className="space-y-4" data-testid="mhm-finance-summary">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              <KPI label="Revenue" value={summary.revenue} money testid="mhm-fin-kpi-revenue"/>
              <KPI label="COD Pending" value={summary.cod_pending} money testid="mhm-fin-kpi-cod-pending"/>
              <KPI label="COD Collected" value={summary.cod_collected} money testid="mhm-fin-kpi-cod-collected"/>
              <KPI label="Agent payouts due" value={summary.agent_payouts_due} money testid="mhm-fin-kpi-payouts"/>
              <KPI label="Expenses MTD" value={summary.expenses_mtd} money testid="mhm-fin-kpi-expenses"/>
              <KPI label="Courier charges" value={summary.courier_charges} money testid="mhm-fin-kpi-courier"/>
              <KPI label="Refunds" value={summary.refunds_total} money testid="mhm-fin-kpi-refunds"/>
              <KPI label="Profit margin" value={summary.profit_margin} money testid="mhm-fin-kpi-profit"/>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="mhm-fin-chart-revenue">
                <h4 className="font-semibold text-sm mb-1">Daily revenue · 30d</h4>
                <div className="h-[180px]"><ResponsiveContainer><LineChart data={charts?.daily_revenue || []}><CartesianGrid stroke="#eef2f7"/><XAxis dataKey="date" hide/><YAxis tick={{fontSize:10}}/><Tooltip/><Line dataKey="revenue" stroke="#16a34a" dot={false}/></LineChart></ResponsiveContainer></div>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="mhm-fin-chart-cod">
                <h4 className="font-semibold text-sm mb-1">COD trend · 30d</h4>
                <div className="h-[180px]"><ResponsiveContainer><BarChart data={charts?.cod_trend || []}><CartesianGrid stroke="#eef2f7"/><XAxis dataKey="date" hide/><YAxis tick={{fontSize:10}}/><Tooltip/><Legend/><Bar dataKey="collected" fill="#22c55e"/><Bar dataKey="pending" fill="#f59e0b"/></BarChart></ResponsiveContainer></div>
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl p-3" data-testid="mhm-fin-chart-expenses">
                <h4 className="font-semibold text-sm mb-1">Expense breakdown · 30d</h4>
                <div className="h-[180px]"><ResponsiveContainer><BarChart data={charts?.expense_breakdown || []}><CartesianGrid stroke="#eef2f7"/><XAxis dataKey="category" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}}/><Tooltip/><Bar dataKey="amount" fill="#7c3aed"/></BarChart></ResponsiveContainer></div>
              </div>
            </div>
          </div>
        )}

        {tab === "COD" && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-finance-cod">
            {selected.size > 0 && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-2 flex gap-2 items-center mb-2" data-testid="mhm-finance-cod-bulk">
                <span className="text-sm">{selected.size} selected</span>
                <button onClick={() => codBulk("settled")} className="text-xs px-2 py-1 bg-green-600 text-white rounded" data-testid="mhm-finance-cod-bulk-settled">Mark settled</button>
                <button onClick={() => codBulk("discrepancy")} className="text-xs px-2 py-1 bg-rose-600 text-white rounded">Mark discrepancy</button>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr><th></th><th className="text-left p-1">Entry</th><th className="text-left">Amount</th><th className="text-left">Status</th><th className="text-left">Created</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cod.rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No pending COD entries</td></tr>}
                {cod.rows.map((r) => (
                  <tr key={r.entry_id || r._id}>
                    <td className="p-1"><input type="checkbox" checked={selected.has(r.entry_id)} onChange={(e) => { const n = new Set(selected); e.target.checked ? n.add(r.entry_id) : n.delete(r.entry_id); setSelected(n); }}/></td>
                    <td className="font-mono text-xs">{r.entry_id || r._id}</td>
                    <td>₹{r.amount}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{r.status}</span></td>
                    <td className="text-xs text-slate-500">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Payouts" && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-finance-payouts">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr><th className="text-left p-1">Agent</th><th className="text-left">Email</th><th>Penalties</th><th>Pending</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {payouts.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No agents</td></tr>}
                {payouts.map((a) => (
                  <tr key={a.agent_user_id}>
                    <td className="p-1">{a.name}</td>
                    <td className="text-xs">{a.email}</td>
                    <td className="text-center">{a.penalties_count}</td>
                    <td>₹{a.pending_payout}</td>
                    <td className="text-right">
                      <button onClick={() => setPayoutAgent(a)}
                              data-testid={`mhm-fin-payout-${a.agent_user_id}`}
                              className="text-xs px-2 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded">Pay out</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Expenses" && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-finance-expenses">
            <button onClick={() => setExpenseOpen(true)} className="text-sm px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg mb-2 inline-flex items-center gap-1"
                    data-testid="mhm-finance-expense-add">
              <Plus className="h-4 w-4" /> Add expense
            </button>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr><th className="text-left p-1">Date</th><th className="text-left">Category</th><th className="text-left">Notes</th><th className="text-right">Amount</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenses.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No expenses</td></tr>}
                {expenses.map((e) => (
                  <tr key={e.expense_id}>
                    <td className="p-1 text-xs">{e.incurred_at}</td>
                    <td>{e.category}</td>
                    <td className="text-xs text-slate-600">{e.notes || "—"}</td>
                    <td className="text-right">₹{e.amount}</td>
                    <td className="text-right">
                      <button onClick={async () => {
                          if (!window.confirm("Delete expense?")) return;
                          await api.delete(`/hub/finance/expenses/${e.expense_id}`);
                          toast.success("Deleted"); load();
                        }} className="text-xs text-rose-600 hover:underline">delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Refunds" && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-finance-refunds">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr><th className="text-left p-1">Tracking</th><th>Amount</th><th>Status</th><th>Reason</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {refunds.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No refunds</td></tr>}
                {refunds.map((r) => (
                  <tr key={r.refund_id || r._id}>
                    <td className="p-1 text-xs font-mono"><TrackingIdLink tid={r.tracking_id} /></td>
                    <td>₹{r.amount}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{r.status}</span></td>
                    <td className="text-xs">{r.reason || "—"}</td>
                    <td className="text-right text-xs space-x-1">
                      <button onClick={() => decideRefund(r, "approve")} className="px-2 py-1 bg-green-600 text-white rounded">approve</button>
                      <button onClick={() => decideRefund(r, "reject")} className="px-2 py-1 bg-rose-600 text-white rounded">reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "Damage" && (
          <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="mhm-finance-damage">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 bg-slate-50">
                <tr><th className="text-left p-1">Claim</th><th>Tracking</th><th>Type</th><th>Status</th><th></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {damage.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No claims</td></tr>}
                {damage.map((d) => (
                  <tr key={d.claim_id}>
                    <td className="p-1 font-mono text-xs">{d.claim_id}</td>
                    <td className="text-xs"><TrackingIdLink tid={d.tracking_id} /></td>
                    <td>{d.claim_type}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{d.status}</span></td>
                    <td className="text-right text-xs space-x-1">
                      <button onClick={() => decideDamage(d, "approved")} className="px-2 py-1 bg-green-600 text-white rounded">approve</button>
                      <button onClick={() => decideDamage(d, "rejected")} className="px-2 py-1 bg-rose-600 text-white rounded">reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ExpenseModal open={expenseOpen} onClose={() => setExpenseOpen(false)} onCreated={load}/>
      <PayoutModal open={!!payoutAgent} onClose={() => setPayoutAgent(null)} onCreated={load} agent={payoutAgent}/>
    </RoleGate>
  );
}
